import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, FileText, X } from "lucide-react";
import { toast } from "sonner";

const NEW_DRINK = "__new_drink__";
const LANG_LABELS = { en: "Inglese", de: "Tedesco", fr: "Francese", es: "Spagnolo", pt: "Portoghese", nl: "Olandese", it: "Italiano" };

const PdfImportDialog = ({ open, onClose, onApplied, menus = [] }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]);
  const [includeDesc, setIncludeDesc] = useState(false);
  const [done, setDone] = useState(null);

  const foodMenu = menus.find((m) => m.type === "food" && !m.is_daily) || menus.find((m) => m.type !== "bevande" && !m.is_daily) || menus.find((m) => m.type !== "bevande") || menus[0];
  const drinkMenu = menus.find((m) => m.type === "bevande");

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("include_descriptions", includeDesc ? "true" : "false");
      const res = await api.post("/import/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parsed = (res.data.categories || []).map((c) => {
        const isDrink = c.type === "drink";
        const menuId = isDrink ? (drinkMenu?.id || NEW_DRINK) : (foodMenu?.id || "");
        return {
          name: c.name || "",
          type: c.type || "food",
          menuId,
          dishes: (c.dishes || []).map((d) => ({
            name: d.name || "",
            price: d.price ?? "",
            description: d.description || "",
            formats: Array.isArray(d.formats) ? d.formats : [],
          })),
        };
      });
      if (parsed.length === 0) { toast.error("Nessuna categoria trovata"); }
      setCats(parsed);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'estrazione");
    } finally { setLoading(false); e.target.value = ""; }
  };

  const setCatName = (i, v) => setCats((cs) => cs.map((c, idx) => idx === i ? { ...c, name: v } : c));
  const setCatMenu = (i, v) => setCats((cs) => cs.map((c, idx) => idx === i ? { ...c, menuId: v } : c));
  const setDish = (ci, di, k, v) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: c.dishes.map((d, j) => j === di ? { ...d, [k]: v } : d) }));
  const removeDish = (ci, di) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: c.dishes.filter((_, j) => j !== di) }));
  const removeCat = (ci) => setCats((cs) => cs.filter((_, idx) => idx !== ci));
  const addDish = (ci) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: [...c.dishes, { name: "", price: "", description: "", formats: [] }] }));
  const setFormat = (ci, di, fi, k, v) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: c.dishes.map((d, j) => j !== di ? d : { ...d, formats: d.formats.map((f, k2) => k2 !== fi ? f : { ...f, [k]: v }) }) }));
  const addFormat = (ci, di) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: c.dishes.map((d, j) => j !== di ? d : { ...d, formats: [...(d.formats || []), { label: "", price: "" }] }) }));
  const removeFormat = (ci, di, fi) => setCats((cs) => cs.map((c, idx) => idx !== ci ? c : { ...c, dishes: c.dishes.map((d, j) => j !== di ? d : { ...d, formats: d.formats.filter((_, k2) => k2 !== fi) }) }));

  const apply = async () => {
    const usable = cats.filter((c) => c.name.trim() && c.dishes.some((d) => d.name.trim()));
    if (usable.length === 0) return toast.error("Niente da importare");
    setLoading(true);
    try {
      let drinkId = drinkMenu?.id || null;
      if (usable.some((c) => c.menuId === NEW_DRINK) && !drinkId) {
        const r = await api.post("/menus", { name: "Drink List", type: "bevande", is_daily: false, visible: true, cover_image: null });
        drinkId = r.data.id;
      }
      const payload = {
        categories: usable.map((c) => ({
          name: c.name,
          menu_id: c.menuId === NEW_DRINK ? drinkId : c.menuId,
          dishes: c.dishes.filter((d) => d.name.trim()).map((d) => {
            const fmts = (d.formats || []).filter((f) => (f.label || "").trim() || f.price !== "").map((f) => ({ label: (f.label || "").trim(), price: Number(f.price) || 0 }));
            return {
              name: d.name,
              description: (d.description || "").trim(),
              price: fmts.length ? null : (d.price === "" ? null : Number(d.price)),
              formats: fmts,
            };
          }),
        })),
      };
      const res = await api.post("/import/apply", payload);
      let langs = [];
      try { const rest = await api.get("/restaurant"); langs = (rest.data.settings?.languages || []).filter((l) => l !== "it"); } catch (_) {}
      const upd = res.data.dishes_updated || 0;
      toast.success(`Importati ${res.data.dishes_created} piatti in ${res.data.categories_created} nuove categorie${upd ? ` · ${upd} aggiornati` : ""}`);
      setDone({ dishes: res.data.dishes_created, cats: res.data.categories_created, updated: upd, skipped: res.data.dishes_skipped || 0, updatedDetails: res.data.updated_details || [], dishIds: res.data.dish_ids || [], langs });
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'importazione"); }
    finally { setLoading(false); }
  };

  const translateNow = async () => {
    setLoading(true);
    try {
      const res = await api.post("/dishes/translate-bulk", { dish_ids: done.dishIds });
      toast.success(`Tradotti ${res.data.translated} piatti in ${(res.data.languages || []).length} lingue`);
      onApplied();
    } catch (e) { toast.error("Errore nella traduzione"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="pdf-import-dialog">
        <DialogHeader><DialogTitle>Importa menù da PDF</DialogTitle></DialogHeader>
        {step === 1 && (
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">Carica il PDF del tuo menù completo: estraiamo tutte le categorie, i piatti e i prezzi dell'intero documento e li distribuiamo automaticamente nei menù giusti (cibo e bevande). Il PDF non verrà mai pubblicato — potrai controllare e correggere tutto prima di salvare.</p>
            <label className="flex items-start gap-3 mb-4 cursor-pointer rounded-xl border border-border p-3 hover:bg-muted/40 transition-colors">
              <input type="checkbox" checked={includeDesc} onChange={(e) => setIncludeDesc(e.target.checked)} data-testid="include-desc-checkbox" className="mt-0.5 w-4 h-4 accent-[#002FA7]" />
              <span className="text-sm"><span className="font-medium">Importa anche le descrizioni</span> dei piatti (in italiano). Le traduzioni in altre lingue vengono ignorate; potrai tradurle in automatico dopo.</span>
            </label>
            <label className="flex flex-col items-center gap-2 border-2 border-dashed border-border rounded-2xl py-10 cursor-pointer hover:border-[#002FA7] transition-colors" data-testid="pdf-dropzone">
              {loading ? <><Loader2 className="w-7 h-7 animate-spin text-[#002FA7]" /><span className="text-sm text-muted-foreground">Analisi del PDF in corso…</span></> : <><FileText className="w-8 h-8 text-muted-foreground" /><span className="text-sm font-medium">Seleziona un PDF</span></>}
              <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="pdf-input" disabled={loading} />
            </label>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Controlla tutto prima di importare: assegna ogni categoria al menù corretto, correggi nomi e prezzi. Foto, allergeni e traduzioni si aggiungono dopo.</p>
            {cats.map((c, ci) => (
              <div key={ci} className="rounded-xl border border-border p-3" data-testid={`import-cat-${ci}`}>
                <div className="flex items-center gap-2 mb-2">
                  <input value={c.name} onChange={(e) => setCatName(ci, e.target.value)} data-testid={`import-cat-name-${ci}`} placeholder="Categoria" className="flex-1 font-semibold rounded-lg border border-input px-3 py-1.5 focus:outline-none focus:border-[#002FA7]" />
                  <button onClick={() => removeCat(ci)} className="p-1.5 text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-muted-foreground">Aggiungi al menù:</span>
                  <select value={c.menuId} onChange={(e) => setCatMenu(ci, e.target.value)} data-testid={`import-cat-menu-${ci}`} className="flex-1 rounded-lg border border-input px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#002FA7]">
                    {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    {!drinkMenu && <option value={NEW_DRINK}>➕ Crea menù “Drink List”</option>}
                  </select>
                </div>
                {c.dishes.map((d, di) => (
                  <div key={di} className="mb-2">
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <input value={d.name} onChange={(e) => setDish(ci, di, "name", e.target.value)} data-testid={`import-dish-name-${ci}-${di}`} placeholder="Piatto" className="w-full rounded-lg border border-input px-3 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]" />
                      </div>
                      {(!d.formats || d.formats.length === 0) && (
                        <input value={d.price} onChange={(e) => setDish(ci, di, "price", e.target.value)} type="number" step="0.01" placeholder="€" className="w-20 rounded-lg border border-input px-2 py-1.5 text-sm focus:outline-none focus:border-[#002FA7]" />
                      )}
                      <button onClick={() => removeDish(ci, di)} className="p-1.5 text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {d.formats && d.formats.length > 0 && (
                      <div className="mt-1.5 pl-1 space-y-1.5" data-testid={`import-dish-formats-${ci}-${di}`}>
                        <p className="text-[11px] text-muted-foreground">Varianti di prezzo (etichetta + €):</p>
                        {d.formats.map((f, fi) => (
                          <div key={fi} className="flex gap-2 items-center">
                            <input value={f.label} onChange={(e) => setFormat(ci, di, fi, "label", e.target.value)} placeholder="Es. 0,33 lit / al calice / 100 gr" data-testid={`import-format-label-${ci}-${di}-${fi}`} className="flex-1 rounded-lg border border-input px-2 py-1 text-xs focus:outline-none focus:border-[#002FA7]" />
                            <input value={f.price} onChange={(e) => setFormat(ci, di, fi, "price", e.target.value)} type="number" step="0.01" placeholder="€" data-testid={`import-format-price-${ci}-${di}-${fi}`} className="w-16 rounded-lg border border-input px-2 py-1 text-xs focus:outline-none focus:border-[#002FA7]" />
                            <button onClick={() => removeFormat(ci, di, fi)} data-testid={`import-remove-format-${ci}-${di}-${fi}`} className="p-1 text-destructive"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                        <button onClick={() => addFormat(ci, di)} data-testid={`import-add-format-${ci}-${di}`} className="text-[11px] text-[#002FA7] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Variante</button>
                      </div>
                    )}
                    {(!d.formats || d.formats.length === 0) && (
                      <button onClick={() => addFormat(ci, di)} data-testid={`import-add-format-empty-${ci}-${di}`} className="text-[11px] text-[#002FA7] font-semibold flex items-center gap-1 mt-1 pl-1"><Plus className="w-3 h-3" /> Aggiungi varianti di prezzo</button>
                    )}
                    {includeDesc && (
                      <input value={d.description || ""} onChange={(e) => setDish(ci, di, "description", e.target.value)} data-testid={`import-dish-desc-${ci}-${di}`} placeholder="Descrizione (italiano, facoltativa)" className="w-full mt-1 rounded-lg border border-input px-3 py-1.5 text-xs text-muted-foreground focus:outline-none focus:border-[#002FA7]" />
                    )}
                  </div>
                ))}
                <button onClick={() => addDish(ci)} className="text-xs text-[#002FA7] font-semibold flex items-center gap-1 mt-1"><Plus className="w-3 h-3" /> Piatto</button>
              </div>
            ))}
          </div>
        )}
        {step === 3 && done && (
          <div className="py-6 text-center" data-testid="import-done">
            <p className="text-base font-semibold">Import completato ✓</p>
            <p className="text-sm text-muted-foreground mt-1">{done.dishes} piatti in {done.cats} nuove categorie.{done.updated ? ` ${done.updated} piatti esistenti aggiornati (prezzi/descrizioni).` : ""}{done.skipped ? ` ${done.skipped} già presenti e invariati.` : ""} Tutto è in bozza: aggiungi foto e allergeni e pubblica quando vuoi.</p>
            {done.updatedDetails?.length > 0 && (
              <div className="mt-4 text-left rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="pdf-updated-report">
                <p className="text-sm font-semibold text-amber-800 mb-2">Piatti aggiornati ({done.updatedDetails.length})</p>
                <ul className="space-y-1.5">
                  {done.updatedDetails.map((u, i) => (
                    <li key={i} className="text-xs text-amber-900 flex flex-wrap items-center gap-x-2" data-testid={`pdf-updated-row-${i}`}>
                      <span className="font-medium">{u.name}</span>
                      <span className="text-amber-700">· {u.category}</span>
                      {u.price_changed && (u.old_price !== u.new_price) && (
                        <span className="font-mono">{u.old_price} → <span className="font-semibold">{u.new_price}</span></span>
                      )}
                      {u.desc_changed && <span className="italic">descrizione aggiornata</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {done.langs.length > 0 && (
              <p className="text-sm mt-4">Vuoi tradurre subito <span className="font-medium">nomi e descrizioni</span> in <span className="font-medium">{done.langs.map((l) => LANG_LABELS[l] || l).join(", ")}</span>?</p>
            )}
          </div>
        )}
        <DialogFooter>
          {step !== 3 && <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">Annulla</button>}
          {step === 2 && <button onClick={apply} disabled={loading} data-testid="pdf-apply-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60 flex items-center gap-2">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Importa</button>}
          {step === 3 && done?.langs?.length > 0 && <button onClick={translateNow} disabled={loading} data-testid="pdf-translate-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] disabled:opacity-60 flex items-center gap-2">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Traduci automaticamente</button>}
          {step === 3 && <button onClick={onApplied} data-testid="pdf-done-close" className="rounded-full px-5 py-2 text-sm font-medium hover:bg-muted">{done?.langs?.length > 0 ? "No, chiudi" : "Chiudi"}</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PdfImportDialog;
